import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { StudioRecipe } from '@sr-test-wizard/shared';
import {
  studioRecipesApi,
  type ConfluenceSyncResponse,
  type SourceStatusResponse,
} from '../../lib/studioRecipesApi';
import { useJiraHost } from '../../lib/jiraLinks';
import { ConfluencePickerDialog } from './ConfluencePickerDialog';

interface Props {
  slug: string;
  recipe: StudioRecipe;
  /**
   * Called after a successful sync. The parent uses this to replace the
   * recipe currently loaded in the editor with the freshly-drafted one so
   * the form fields reflect the new AI output.
   */
  onSynced: (result: ConfluenceSyncResponse) => void;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * Source-of-truth panel for a studio's Confluence link. Two modes:
 *   - Unlinked: shows a "Connect to Confluence" CTA that opens the picker
 *     modal. The PDF flow remains available via the separate Add-studio
 *     panel; this is intentionally additive.
 *   - Linked: shows page title, last-sync timestamp, an Open-in-Confluence
 *     link, a Sync-now button (which re-opens the picker pre-filled),
 *     a stale-badge polled from `/source-status`, and a collapsed preview
 *     of the AI-generated client brief.
 */
export function ConfluencePanel({ slug, recipe, onSynced }: Props) {
  const [showPicker, setShowPicker] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [status, setStatus] = useState<SourceStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const jiraHost = useJiraHost();

  const source = recipe.source;
  const isConfluenceLinked = source?.kind === 'confluence' && !!source.confluencePageId;
  const confluenceWebUrl =
    isConfluenceLinked && jiraHost && source?.confluencePageId
      ? `${jiraHost.replace(/\/$/, '')}/wiki/pages/viewpage.action?pageId=${encodeURIComponent(
          source.confluencePageId,
        )}`
      : null;

  // Poll the upstream version once per mount + once after every sync so the
  // stale badge stays honest. We keep it manual (no setInterval) because
  // the user clicks Sync explicitly anyway.
  const refreshStatus = async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const data = await studioRecipesApi.sourceStatus(slug);
      setStatus(data);
    } catch (err) {
      setStatusError((err as Error).message);
    } finally {
      setStatusLoading(false);
    }
  };
  useEffect(() => {
    if (isConfluenceLinked) void refreshStatus();
    else setStatus(null);
    // We intentionally re-fetch when the linked page changes (which mutates
    // the recipe object identity in the parent store).
  }, [slug, source?.confluencePageId, source?.confluenceVersion]);

  const onSyncedLocal = (result: ConfluenceSyncResponse) => {
    setShowPicker(false);
    onSynced(result);
    void refreshStatus();
  };

  return (
    <>
      <div className="card" style={{ borderColor: 'var(--accent)' }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Confluence source</h3>
          {isConfluenceLinked && (
            <span className="badge small info" title="Recipe last drafted from this Confluence page.">
              Linked
            </span>
          )}
          {status?.stale && (
            <span
              className="badge small bad"
              title="The Confluence page has a newer version than what was last synced."
            >
              Stale ({status.localVersion} → {status.upstreamVersion})
            </span>
          )}
          <span className="spacer" />
          {isConfluenceLinked ? (
            <>
              <button
                type="button"
                className="btn small"
                onClick={() => void refreshStatus()}
                disabled={statusLoading}
              >
                {statusLoading ? 'Checking...' : 'Check for updates'}
              </button>
              <button
                type="button"
                className="btn small btn-primary"
                onClick={() => setShowPicker(true)}
                title="Re-pull the page and regenerate the recipe + brief."
              >
                Sync now
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn small btn-primary"
              onClick={() => setShowPicker(true)}
            >
              Connect to Confluence
            </button>
          )}
        </div>

        {isConfluenceLinked && source && (
          <div className="flex-col" style={{ gap: 4, marginTop: 8 }}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span className="muted small">Page:</span>
              <strong>{source.confluencePageTitle || `(id ${source.confluencePageId})`}</strong>
              {confluenceWebUrl && (
                <a
                  href={confluenceWebUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="muted small"
                >
                  Open in Confluence ↗
                </a>
              )}
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span className="muted small">Last synced:</span>
              <span className="small">{formatTimestamp(source.lastSyncedAt)}</span>
              <span className="muted small">·</span>
              <span className="muted small">Version: {source.confluenceVersion ?? '?'}</span>
              {source.confluenceSpaceKey && (
                <>
                  <span className="muted small">·</span>
                  <span className="muted small">Space: {source.confluenceSpaceKey}</span>
                </>
              )}
            </div>
            {statusError && (
              <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>
                Status check failed: {statusError}
              </p>
            )}
          </div>
        )}

        {!isConfluenceLinked && (
          <p className="muted small" style={{ margin: '8px 0 0 0' }}>
            Connect this studio to its Confluence wiki page. We will AI-draft the recipe and
            generate a short client brief that gets injected into every downstream AI call as
            studio context. The existing PDF upload flow remains available - both feed the same
            recipe.
          </p>
        )}

        {recipe.clientBriefMd && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn small btn-ghost"
              onClick={() => setShowBrief((v) => !v)}
            >
              {showBrief ? 'Hide' : 'Show'} client brief ({recipe.clientBriefMd.length} chars)
            </button>
            {showBrief && (
              <div
                className="card"
                style={{ marginTop: 8, maxHeight: 320, overflowY: 'auto', background: 'var(--bg)' }}
              >
                <ReactMarkdown>{recipe.clientBriefMd}</ReactMarkdown>
              </div>
            )}
          </div>
        )}
      </div>

      {showPicker && (
        <ConfluencePickerDialog
          slug={slug}
          initialQuery={recipe.studio || slug}
          onClose={() => setShowPicker(false)}
          onSynced={onSyncedLocal}
        />
      )}
    </>
  );
}
